import { usePathname, useRouter } from 'expo-router';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo } from 'react';
import { useAuth } from 'shared/providers/auth';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';

interface DecodedIdToken {
  'custom:user_account_closed'?: string;
}

const isUserAccountClosed = (idToken: string): boolean => {
  try {
    return (
      jwtDecode<DecodedIdToken>(idToken)['custom:user_account_closed'] ===
      'true'
    );
  } catch {
    logBreadcrumb({
      level: 'warning',
      message: 'Failed to decode idToken while checking closed account status',
    });
    return false;
  }
};

/**
 * Customers with a closed account are only allowed to access /reactivation
 * and the reapplication flow under /signup. Any other authenticated page
 * reached via deep link or bookmark is blocked by redirecting back to
 * /reactivation.
 *
 * Closed-account status is decoded from the idToken's `custom:user_account_closed`
 * claim rather than a GraphQL query, since idToken is always available for an
 * authenticated user and this avoids an extra network request on every route change.
 */
function useRedirectClosedAccount() {
  const { idToken } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  const isAccountClosed = useMemo(
    () => !!idToken && isUserAccountClosed(idToken),
    [idToken],
  );

  useEffect(() => {
    const isAllowedForClosedAccount =
      pathname.startsWith('/reactivation') || pathname.startsWith('/signup');

    if (isAccountClosed && !isAllowedForClosedAccount) {
      router.replace('/reactivation');
    }
  }, [isAccountClosed, pathname, router]);
}

export default useRedirectClosedAccount;
